import { ActionTree, GetterTree, MutationTree } from 'vuex';
import _ from 'lodash';
import { Table } from '@/main/utils/table';
import { BaseApi } from '@/main/baseApi';
import { editActions } from '@/main/store/pages/edit/actions';
import { listEditGetters } from '@/main/store/pages/getters';
import { mutations as pageMutations } from '@/main/store/pages/page/mutations';

import { storesMap } from '../../registry';
import { DropdownList } from '../../@types/dropdown.d';
import { DepTables, EditStore, EditStoreProps, EditStoreBase } from '../../@types/edit.d';
import { PageModule, RootStore, DependetTableSetting } from '../../@types/store.d';
import { editName } from '../../listEditTypes';
import { pageStoreBuilder, pageStateBuilder } from '../page/builder';
import { mutations } from './mutations';


export type EditPageModule<State extends EditStoreBase,
	ExtraActions,
	ExtraMutations,
	ExtraGetters> = PageModule<State,
	ExtraActions & ReturnType<typeof editActions>,
	ExtraMutations & typeof pageMutations & typeof mutations,
	ExtraGetters & typeof listEditGetters>;

export function editStoreBuilder<
	Provider extends BaseApi,
	EditState extends object,
	TDropdownList extends DropdownList,
	LocalState extends object,
	DependentTableNames extends readonly string[],
	// TODO: typ GetterTree<EditStore<EditState, TDropdownList, LocalState, DepTables<DependentTableNames>, Props>, RootStore>,
	Props extends EditStoreProps,
	ExtraActions extends ActionTree<EditStore<EditState, TDropdownList, LocalState, DepTables<DependentTableNames>, Props>, RootStore>,
	ExtraMutations extends MutationTree<EditStore<EditState, TDropdownList, LocalState, DepTables<DependentTableNames>, Props>>,
	ExtraGetters extends GetterTree<EditStore<EditState, TDropdownList, LocalState, DepTables<DependentTableNames>, Props>, RootStore>,
>({
	name,
	apiProvider,
	crumbLabel,
	dropdownList,
	depTableNames,
	depTableSettings,
	edit,
	localState,
	props,
	extraActions,
	extraMutations,
	extraGetters,
}: {
	name: string,
	apiProvider: Provider | (() => Provider),
	crumbLabel: string,
	dropdownList: TDropdownList,
	depTableNames: DependentTableNames,
	depTableSettings: Record<string, DependetTableSetting<EditStore<EditState, TDropdownList, LocalState, DepTables<DependentTableNames>, Props>>>,
	edit: EditState,
	localState: LocalState,
	props: Props,
	extraActions: ExtraActions,
	extraMutations: ExtraMutations,
	extraGetters: ExtraGetters,
}) {
	const pageStore = pageStoreBuilder({
		name: editName(name),
		crumbLabel,
		dropdownList,
		localState,
		props,
	});

	const module: PageModule<
	EditStore<EditState, TDropdownList, LocalState, DepTables<DependentTableNames>, Props>,
	ExtraActions & typeof pageStore.actions & ReturnType<typeof editActions>,
	ExtraMutations & typeof pageStore.mutations & typeof mutations,
	ExtraGetters & typeof pageStore.getters & typeof listEditGetters
	> = {
		namespaced: true,
		actions: {
			...pageStore.actions,
			...editActions({
				apiProvider: (_.isFunction(apiProvider) ? apiProvider : () => apiProvider) as () => BaseApi,
				depTableNames,
			}),
			...extraActions,
		},
		getters: {
			...pageStore.getters,
			...listEditGetters,
			...extraGetters,
			..._.reduce(depTableSettings, (result, value, key) => ({ ...result, [`${key}Filter`]: value.filter }), {}),
		},
		mutations: { ...pageStore.mutations, ...mutations, ...extraMutations },
		state(): EditStore<EditState, TDropdownList, LocalState, DepTables<DependentTableNames>, Props> {
			return editStateBuilder({
				name: editName(name),
				crumbLabel,
				dropdownList,
				depTableNames,
				depTableSettings,
				edit,
				localState,
				props,
			});
		},
	};
	storesMap.set(editName(name), _.cloneDeep(module));

	return module;
}

export function editStateBuilder<
	EditState extends object,
	TDropdownList extends DropdownList,
	LocalState extends object,
	DependentTableNames extends readonly string[],
	Props extends EditStoreProps,
>({
	name,
	crumbLabel,
	dropdownList,
	depTableNames,
	depTableSettings,
	edit,
	localState,
	props,
}: {
	name: string,
	crumbLabel: string,
	dropdownList: TDropdownList,
	depTableNames: DependentTableNames,
	depTableSettings: Record<string, DependetTableSetting<EditStore<EditState, TDropdownList, LocalState, DepTables<DependentTableNames>, Props>>>,
	edit: EditState,
	localState: LocalState,
	props: Props,
}) {
	return {
		...pageStateBuilder({ name, crumbLabel, dropdownList, localState, props }),
		dependentTables: depTableNames.reduce(
			(tables: Record<DependentTableNames[number], Table>, tableName: DependentTableNames[number]) => {
				const setting = depTableSettings[tableName];
				tables[tableName] = new Table();
				tables[tableName].name = tableName;
				tables[tableName].isEditable = setting && (setting.isEditable ?? false);
				tables[tableName].loadByEntityId = setting && (setting.loadByEntityId ?? false);
				tables[tableName].columnSettings = setting && (setting.columns ?? null);
				return tables;
			},
			{} as Record<DependentTableNames[number], Table>,
		),
		dmsTabs: [],
		edit: _.cloneDeep(edit),
		editHash: null,
		entityId: null,
	};
}
